import { useMemo } from "react"
import { waitFor } from "xstate"
import { useSelector } from "@xstate/react"
import { HomeIcon, LogInIcon } from "lucide-react"
import { useRouteError, useSearchParams } from "react-router"

import { authMachine } from "../machines/Authmachine"
import { getAuthErrorMessage, getAuthStateMessage } from "@recordsure/unity-auth-machine"
import { Button } from "@/components/ui/button"

async function loader() {
  await waitFor(authMachine, (state) => state.hasTag("Settled"))
  return null
}

export function Auth() {
  const [searchParams] = useSearchParams()

  const code = useSelector(authMachine, (state) => state.context.error)
  const isTokenRevoked = useSelector(authMachine, (state) => state.context.tokenRevoked)
  const isAuthenticated = useSelector(authMachine, (snapshot) => snapshot.matches("authenticated"))

  const message = useMemo(() => {
    if (code == null) return getAuthStateMessage(isAuthenticated)
    return getAuthErrorMessage(code)
  }, [code, isAuthenticated])

  return (
    <div className="grid h-screen w-screen place-items-center">
      <div className="flex max-w-sm flex-col items-center gap-2">
        {isTokenRevoked === false && (
          <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-4 shadow">
            <h5 className="text-sm font-semibold tracking-wide">Authentication Error:</h5>
            <p className="text-xs">Error revoking auth tokens.</p>
          </div>
        )}
        <a href="/" className="block">
          <img src="/logo.png" alt="Recordsure Logo" className="w-full max-w-[10rem]" />
        </a>
        <h4 className="text-center text-3xl font-bold">{message.main}</h4>
        <p className="text-center text-lg text-balance">{message.content}</p>
        {message.subtitle != null && message.subtitle !== "" && (
          <p className="text-center text-balance">{message.subtitle}</p>
        )}
        {isAuthenticated ? (
          <a href="/" className="btn btn-neutral flex gap-2 normal-case">
            <HomeIcon />
            Home
          </a>
        ) : (
          <Button
            type="submit"
            aria-label="Log In"
            className="btn btn-neutral flex gap-2 normal-case"
            onClick={() => authMachine.send({ type: "LOGIN", path: searchParams.get("from") })}
          >
            <LogInIcon />
            Log In
          </Button>
        )}
      </div>
    </div>
  )
}

function ErrorBoundary() {
  const [searchParams] = useSearchParams()
  const error = useRouteError() as { message: string }
  console.error(error)

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center gap-7 p-4">
      <a href="/" className="block">
        <img src="/logo.png" alt="Recordsure Logo" className="h-20 w-20" />
      </a>
      <h2 className="text-center text-3xl font-bold text-black">{error.message ?? "Something went wrong!!"}</h2>
      <button
        type="button"
        className="btn btn-accent"
        onClick={() => authMachine.send({ type: "LOGIN", path: searchParams.get("from") })}
      >
        Try again
      </button>
    </div>
  )
}

Auth.routeId = "auth"
Auth.loader = loader
Auth.ErrorBoundary = ErrorBoundary
